<template>
  <HrPageLayout
    title="Department Management"
    subtitle="Manage departments and organization structure"
    icon="🏢"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'Departments' }
    ]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-primary" @click="openAddModal">
        <i class="bx bx-plus"></i> Add Department
      </button>
      <button class="hr-btn hr-btn-success" @click="downloadReport">
        <i class="bx bxs-download"></i> Download
      </button>
    </template>

    <div class="department-management-content">
      <!-- Stats cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-building"></i></div>
          <p class="hr-metric-label">Total Departments</p>
          <h2 class="hr-metric-value">{{ stats.totalDepartments }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <p class="hr-metric-label">Active Departments</p>
          <h2 class="hr-metric-value">{{ stats.activeDepartments }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-group"></i></div>
          <p class="hr-metric-label">Total Employees</p>
          <h2 class="hr-metric-value">{{ stats.totalEmployees }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-dollar"></i></div>
          <p class="hr-metric-label">Average Budget</p>
          <h2 class="hr-metric-value">{{ formatCurrency(stats.averageBudget) }}</h2>
        </div>
      </div>

      <!-- Search and filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-3">
          <div class="hr-filters-row">
            <div class="hr-search-wrap">
              <input v-model="search" @input="filterDepartments" class="hr-input"
                     placeholder="Search departments..." spellcheck="false" type="text">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Location</label>
              <select v-model="selectedLocation" @change="filterDepartments" class="hr-select" style="min-width: 200px;">
                <option value="">All Locations</option>
                <option v-for="location in locations" :key="location" :value="location">{{ location }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Department data table -->
      <div class="hr-chart-card">
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover hr-table-striped">
              <thead>
                <tr>
                  <th>Department Name</th>
                  <th>Vote</th>
                  <th>Location</th>
                  <th>Authorised Chief Officer</th>
                  <th>Employees</th>
                  <th>Budget</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="8" class="hr-text-center hr-py-4">
                    <div class="hr-spinner"></div>
                  </td>
                </tr>
                <tr v-else-if="filteredDepartments.length === 0">
                  <td colspan="8" class="hr-text-center hr-py-4 hr-text-muted">
                    No departments found matching your criteria
                  </td>
                </tr>
                <tr v-for="department in filteredDepartments" :key="department.id">
                  <td>
                    <div class="dept-cell-name">
                      <div class="dept-avatar">{{ department.departmentName.charAt(0).toUpperCase() }}</div>
                      <div>
                        <strong>{{ department.departmentName }}</strong>
                        <p class="hr-text-muted hr-mb-0 hr-small">{{ department.description || 'No description' }}</p>
                      </div>
                    </div>
                  </td>
                  <td><span class="hr-badge hr-badge-secondary">{{ department.code || '—' }}</span></td>
                  <td>{{ department.location || 'Not specified' }}</td>
                  <td>
                    <div v-if="department.manager">
                      <strong>{{ department.manager }}</strong>
                      <p class="hr-text-muted hr-mb-0 hr-small"><i class="bx bx-envelope"></i> {{ department.managerEmail || '' }}</p>
                    </div>
                    <span v-else class="hr-text-muted">No Authorised Chief Officer assigned</span>
                  </td>
                  <td><span class="hr-badge hr-badge-info">{{ department.employeeCount || 0 }}</span></td>
                  <td>{{ formatCurrency(department.budget) }}</td>
                  <td>
                    <span :class="'hr-badge ' + getStatusBadgeClass(department.status)">{{ department.status || 'Active' }}</span>
                  </td>
                  <td>
                    <div class="hr-btn-group">
                  <button class="hr-btn hr-btn-sm hr-btn-info" @click="openOrgStructure(department)" title="Org Structure">
                    <i class="bx bx-sitemap"></i>
                  </button>
                      <button class="hr-btn hr-btn-sm hr-btn-primary" @click="viewDepartment(department)">
                        <i class="bx bx-show"></i>
                      </button>
                      <button class="hr-btn hr-btn-sm hr-btn-warning" @click="editDepartment(department)">
                        <i class="bx bx-edit"></i>
                      </button>
                      <button class="hr-btn hr-btn-sm hr-btn-danger" @click="confirmDelete(department)">
                        <i class="bx bx-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.totalPages > 1" class="hr-pagination-wrap hr-mt-4">
            <div class="hr-pagination-info">
              Showing {{ ((pagination.currentPage - 1) * pagination.limit) + 1 }} to
              {{ Math.min(pagination.currentPage * pagination.limit, pagination.totalCount) }}
              of {{ pagination.totalCount }} entries
            </div>
            <nav class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="pagination.currentPage === 1"
                      @click="changePage(pagination.currentPage - 1)">Previous</button>
              <template v-for="page in getVisiblePages()" :key="page">
                <button type="button" class="hr-page-btn"
                        :class="{ 'hr-page-active': page === pagination.currentPage }"
                        @click="changePage(page)">{{ page }}</button>
              </template>
              <button type="button" class="hr-page-btn"
                      :disabled="pagination.currentPage === pagination.totalPages"
                      @click="changePage(pagination.currentPage + 1)">Next</button>
            </nav>
          </div>
        </div>
      </div>

      <!-- Add Department Modal -->
      <ModalWrapper ref="addDepartmentModal" title="Add New Department" size="large">
        <template #body>
          <form @submit.prevent="addDepartment" class="hr-form-grid">
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Department Name <span class="hr-text-danger">*</span></label>
              <input v-model="newDepartment.departmentName" class="hr-input" placeholder="Enter department name" required type="text">
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Vote</label>
              <input v-model="newDepartment.departmentCode" class="hr-input" placeholder="e.g. FIN, HR" type="text">
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Location</label>
              <input v-model="newDepartment.location" class="hr-input" placeholder="Enter location" type="text">
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Authorised Chief Officer</label>
              <select v-model="newDepartment.managerId" @change="onManagerChange('new')" class="hr-select">
                <option value="">Select Authorised Chief Officer (Optional)</option>
                <option v-for="manager in availableManagers" :key="manager.id" :value="manager.id">
                  {{ manager.firstName }} {{ manager.lastName }}
                  <span v-if="manager.designation"> - {{ manager.designation }}</span>
                  <span v-if="manager.departmentName"> ({{ manager.departmentName }})</span>
                </option>
              </select>
              <small class="hr-text-muted hr-d-block hr-mt-1">
                <span v-if="loadingManagers" class="hr-text-info"><i class="bx bx-loader-alt bx-spin"></i> Loading...</span>
                <span v-else-if="selectedManagerInfo.new">
                  Selected: {{ selectedManagerInfo.new.firstName }} {{ selectedManagerInfo.new.lastName }}
                  <br>Email: {{ selectedManagerInfo.new.email || 'Not available' }}
                  <br>Phone: {{ selectedManagerInfo.new.phone || 'Not available' }}
                </span>
              </small>
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Budget</label>
              <div class="hr-budget-input">
                <select v-model="newDepartment.currency" class="hr-select" style="max-width: 90px;">
                  <option value="KES">KES</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
                <input v-model="newDepartment.budget" class="hr-input" placeholder="Amount" type="number" step="0.01">
              </div>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Description</label>
              <textarea v-model="newDepartment.description" class="hr-textarea" placeholder="Enter department description" rows="3"></textarea>
            </div>
          </form>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.addDepartmentModal?.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" @click="addDepartment" :disabled="addingDepartment">
            <span v-if="addingDepartment" class="hr-spinner small"></span>
            <i v-else class="bx bx-check"></i> Add Department
          </button>
        </template>
      </ModalWrapper>

      <!-- Edit Department Modal -->
      <ModalWrapper ref="editDepartmentModal" title="Edit Department" size="large">
        <template #body>
          <form v-if="editingDepartment" @submit.prevent="updateDepartment" class="hr-form-grid">
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Department Name <span class="hr-text-danger">*</span></label>
              <input v-model="editingDepartment.departmentName" class="hr-input" required type="text">
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Vote</label>
              <input v-model="editingDepartment.departmentCode" class="hr-input" placeholder="e.g. FIN, HR" type="text">
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Location</label>
              <input v-model="editingDepartment.location" class="hr-input" type="text">
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Authorised Chief Officer</label>
              <select v-model="editingDepartment.managerId" @change="onManagerChange('edit')" class="hr-select">
                <option value="">Select Authorised Chief Officer (Optional)</option>
                <option v-for="manager in availableManagers" :key="manager.id" :value="manager.id">
                  {{ manager.firstName }} {{ manager.lastName }}
                  <span v-if="manager.designation"> - {{ manager.designation }}</span>
                  <span v-if="manager.departmentName"> ({{ manager.departmentName }})</span>
                </option>
              </select>
              <small v-if="selectedManagerInfo.edit" class="hr-text-muted hr-d-block hr-mt-1">
                Selected: {{ selectedManagerInfo.edit.firstName }} {{ selectedManagerInfo.edit.lastName }}
                <br>Email: {{ selectedManagerInfo.edit.email || 'Not available' }}
                <br>Phone: {{ selectedManagerInfo.edit.phone || 'Not available' }}
              </small>
            </div>
            <div class="hr-filter-group span-half">
              <label class="hr-form-label">Budget</label>
              <div class="hr-budget-input">
                <select v-model="editingDepartment.currency" class="hr-select" style="max-width: 90px;">
                  <option value="KES">KES</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
                <input v-model="editingDepartment.budget" class="hr-input" type="number" step="0.01">
              </div>
            </div>
            <div class="hr-filter-group span-full">
              <label class="hr-form-label">Description</label>
              <textarea v-model="editingDepartment.description" class="hr-textarea" rows="3"></textarea>
            </div>
          </form>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.editDepartmentModal?.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-warning" @click="updateDepartment" :disabled="updatingDepartment">
            <span v-if="updatingDepartment" class="hr-spinner small"></span>
            <i v-else class="bx bx-check"></i> Update Department
          </button>
        </template>
      </ModalWrapper>

      <!-- View Department Modal -->
      <ModalWrapper ref="viewDepartmentModal" title="Department Details" size="large">
        <template #body v-if="selectedDepartment">
          <div class="dept-view-grid">
            <div class="hr-chart-card">
              <div class="hr-chart-header"><h6 class="hr-chart-title hr-mb-0">Basic Information</h6></div>
              <div class="hr-chart-body">
                <div class="dept-view-item"><label class="hr-text-muted">Department Name:</label><p class="hr-mb-0"><strong>{{ selectedDepartment.departmentName }}</strong></p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Vote:</label><p class="hr-mb-0">{{ selectedDepartment.code || '—' }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Location:</label><p class="hr-mb-0">{{ selectedDepartment.location || 'Not specified' }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Budget:</label><p class="hr-mb-0">{{ formatCurrency(selectedDepartment.budget) }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Created:</label><p class="hr-mb-0">{{ formatDate(selectedDepartment.createdAt) }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Last Updated:</label><p class="hr-mb-0">{{ formatDate(selectedDepartment.updatedAt) }}</p></div>
              </div>
            </div>
            <div class="hr-chart-card">
              <div class="hr-chart-header"><h6 class="hr-chart-title hr-mb-0">Authorised Chief Officer Information</h6></div>
              <div class="hr-chart-body">
                <div class="dept-view-item"><label class="hr-text-muted">Authorised Chief Officer:</label><p class="hr-mb-0">{{ selectedDepartment.manager || 'No Authorised Chief Officer assigned' }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Email:</label><p class="hr-mb-0">{{ selectedDepartment.managerEmail || 'Not provided' }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Phone:</label><p class="hr-mb-0">{{ selectedDepartment.managerPhone || 'Not provided' }}</p></div>
                <div class="dept-view-item"><label class="hr-text-muted">Employees:</label><p class="hr-mb-0"><span class="hr-badge hr-badge-info">{{ selectedDepartment.employeeCount || 0 }}</span></p></div>
              </div>
            </div>
          </div>
          <div class="hr-chart-card hr-mt-3">
            <div class="hr-chart-header"><h6 class="hr-chart-title hr-mb-0">Description</h6></div>
            <div class="hr-chart-body"><p class="hr-mb-0">{{ selectedDepartment.description || 'No description provided' }}</p></div>
          </div>
          <div v-if="departmentEmployees.length > 0" class="hr-chart-card hr-mt-3">
            <div class="hr-chart-header hr-flex-between">
              <h6 class="hr-chart-title hr-mb-0">Department Employees</h6>
              <span class="hr-badge hr-badge-primary">{{ departmentEmployees.length }}</span>
            </div>
            <div class="hr-chart-body hr-p-0">
              <div class="hr-table-wrapper">
                <table class="hr-table">
                  <thead><tr><th>Employee ID</th><th>Name</th><th>Position</th><th>Email</th><th>Status</th></tr></thead>
                  <tbody>
                    <tr v-for="employee in departmentEmployees" :key="employee.id">
                      <td>{{ employee.empNo || employee.nationalId }}</td>
                      <td>{{ employee.firstName }} {{ employee.lastName }}</td>
                      <td>{{ employee.title || employee.designation || 'N/A' }}</td>
                      <td>{{ employee.email }}</td>
                      <td><span :class="'hr-badge ' + getEmployeeStatusBadge(employee.status)">{{ employee.status }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.viewDepartmentModal?.close()">Close</button>
          <button type="button" class="hr-btn hr-btn-warning" @click="openEditFromView">
            <i class="bx bx-edit"></i> Edit Department
          </button>
        </template>
      </ModalWrapper>

      <!-- Delete Confirmation Modal -->
      <ModalWrapper ref="deleteConfirmModal" title="Confirm Delete">
        <template #body>
          <div class="hr-text-center">
            <div class="hr-mb-3"><i class="bx bx-error-circle hr-text-danger" style="font-size: 3rem;"></i></div>
            <h5>Are you sure?</h5>
            <p class="hr-mb-0">
              You are about to delete <strong>{{ departmentToDelete?.departmentName }}</strong>.
              This action cannot be undone.
            </p>
            <div v-if="departmentToDelete?.employeeCount > 0" class="hr-alert hr-alert-warning hr-mt-3">
              <strong>Warning:</strong> This department has {{ departmentToDelete.employeeCount }} employees.
              Please reassign them before deleting.
            </div>
          </div>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.deleteConfirmModal?.close()">Cancel</button>
          <button type="button" class="hr-btn hr-btn-danger" @click="deleteDepartment" :disabled="deletingDepartment">
            <span v-if="deletingDepartment" class="hr-spinner small"></span>
            <i v-else class="bx bx-trash"></i> Delete Department
          </button>
        </template>
      </ModalWrapper>

      <!-- Org Structure Modal -->
      <ModalWrapper ref="orgStructureModal" title="Manage Org Structure" size="large">
        <template #body>
          <div v-if="orgStructureDepartment" class="org-structure-grid">
            <div class="hr-chart-card">
              <div class="hr-chart-header">
                <h6 class="hr-chart-title hr-mb-0">Section / Sub-votes</h6>
              </div>
              <div class="hr-chart-body">
                <form @submit.prevent="createSectionForDept" class="hr-form-grid hr-mb-3">
                  <div class="hr-filter-group span-full">
                    <label class="hr-form-label">Name <span class="hr-text-danger">*</span></label>
                    <input v-model="newSection.name" class="hr-input" placeholder="e.g. Finance Section" type="text">
                  </div>
                  <div class="hr-filter-group span-half">
                    <label class="hr-form-label">Code</label>
                    <input v-model="newSection.code" class="hr-input" placeholder="Optional code" type="text">
                  </div>
                  <div class="hr-filter-group span-half">
                    <label class="hr-form-label">Sub-vote Code</label>
                    <input v-model="newSection.sub_vote_code" class="hr-input" placeholder="Optional sub-vote code" type="text">
                  </div>
                  <div class="hr-filter-group span-full hr-text-right">
                    <button type="submit" class="hr-btn hr-btn-primary hr-btn-sm" :disabled="savingSection">
                      <span v-if="savingSection" class="hr-spinner small"></span>
                      <i v-else class="bx bx-plus"></i> Add Section
                    </button>
                  </div>
                </form>

                <div v-if="orgSections.length === 0" class="hr-text-muted hr-small">
                  No sections defined yet for this department.
                </div>
                <ul v-else class="org-list">
                  <li v-for="sec in orgSections" :key="sec.id" class="hr-d-flex hr-align-items-center hr-justify-content-between hr-gap-2">
                    <template v-if="editingSectionId === sec.id">
                      <div class="hr-form-grid hr-flex-grow-1" style="grid-template-columns: 1fr 1fr auto auto;">
                        <input v-model="editSectionForm.name" class="hr-input hr-input-sm" placeholder="Name">
                        <input v-model="editSectionForm.code" class="hr-input hr-input-sm" placeholder="Code">
                        <input v-model="editSectionForm.sub_vote_code" class="hr-input hr-input-sm" placeholder="Sub-vote code">
                        <div class="hr-d-flex hr-gap-1">
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="saveSectionEdit" :disabled="savingSectionEdit">Save</button>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="editingSectionId = null">Cancel</button>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <span><strong>{{ sec.name }}</strong>
                        <span v-if="sec.code" class="org-tag">{{ sec.code }}</span>
                        <span v-if="sec.sub_vote_code" class="org-tag muted">SV: {{ sec.sub_vote_code }}</span></span>
                      <div class="hr-d-flex hr-gap-1">
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="startEditSection(sec)"><i class="bx bx-edit"></i></button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="deleteSectionConfirm(sec)"><i class="bx bx-trash"></i></button>
                      </div>
                    </template>
                  </li>
                </ul>
              </div>
            </div>

            <div class="hr-chart-card">
              <div class="hr-chart-header">
                <h6 class="hr-chart-title hr-mb-0">Units / Directorates</h6>
              </div>
              <div class="hr-chart-body">
                <div class="hr-filter-group span-full hr-mb-2">
                  <label class="hr-form-label">Section</label>
                  <select v-model="selectedSectionIdForUnits" class="hr-select" @change="loadUnitsForSection">
                    <option value="">Select section</option>
                    <option v-for="sec in orgSections" :key="sec.id" :value="sec.id">
                      {{ sec.name }}
                    </option>
                  </select>
                </div>

                <form @submit.prevent="createUnitForSection" class="hr-form-grid hr-mb-3">
                  <div class="hr-filter-group span-full">
                    <label class="hr-form-label">Name <span class="hr-text-danger">*</span></label>
                    <input v-model="newUnit.name" class="hr-input" placeholder="e.g. Revenue Unit" type="text">
                  </div>
                  <div class="hr-filter-group span-half">
                    <label class="hr-form-label">Code</label>
                    <input v-model="newUnit.code" class="hr-input" placeholder="Optional code" type="text">
                  </div>
                  <div class="hr-filter-group span-full hr-text-right">
                    <button type="submit" class="hr-btn hr-btn-primary hr-btn-sm" :disabled="savingUnit || !selectedSectionIdForUnits">
                      <span v-if="savingUnit" class="hr-spinner small"></span>
                      <i v-else class="bx bx-plus"></i> Add Unit
                    </button>
                  </div>
                </form>

                <div v-if="orgUnits.length === 0" class="hr-text-muted hr-small">
                  No units defined for the selected section.
                </div>
                <ul v-else class="org-list">
                  <li v-for="u in orgUnits" :key="u.id" class="hr-d-flex hr-align-items-center hr-justify-content-between hr-gap-2">
                    <template v-if="editingUnitId === u.id">
                      <div class="hr-d-flex hr-gap-2 hr-flex-wrap hr-flex-grow-1">
                        <input v-model="editUnitForm.name" class="hr-input hr-input-sm" placeholder="Name">
                        <input v-model="editUnitForm.code" class="hr-input hr-input-sm" placeholder="Code">
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="saveUnitEdit" :disabled="savingUnitEdit">Save</button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="editingUnitId = null">Cancel</button>
                      </div>
                    </template>
                    <template v-else>
                      <span><strong>{{ u.name }}</strong><span v-if="u.code" class="org-tag">{{ u.code }}</span></span>
                      <div class="hr-d-flex hr-gap-1">
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="startEditUnit(u)"><i class="bx bx-edit"></i></button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="deleteUnitConfirm(u)"><i class="bx bx-trash"></i></button>
                      </div>
                    </template>
                  </li>
                </ul>
              </div>
            </div>

            <div class="hr-chart-card">
              <div class="hr-chart-header">
                <h6 class="hr-chart-title hr-mb-0">Sub-unit</h6>
              </div>
              <div class="hr-chart-body">
                <div class="hr-filter-group span-full hr-mb-2">
                  <label class="hr-form-label">Unit</label>
                  <select v-model="selectedUnitIdForSubUnits" class="hr-select" @change="loadSubUnitsForUnit">
                    <option value="">Select unit</option>
                    <option v-for="u in orgUnits" :key="u.id" :value="u.id">
                      {{ u.name }}
                    </option>
                  </select>
                </div>

                <form @submit.prevent="createSubUnitForUnit" class="hr-form-grid hr-mb-3">
                  <div class="hr-filter-group span-full">
                    <label class="hr-form-label">Name <span class="hr-text-danger">*</span></label>
                    <input v-model="newSubUnit.name" class="hr-input" placeholder="e.g. Payroll Sub Unit" type="text">
                  </div>
                  <div class="hr-filter-group span-half">
                    <label class="hr-form-label">Code</label>
                    <input v-model="newSubUnit.code" class="hr-input" placeholder="Optional code" type="text">
                  </div>
                  <div class="hr-filter-group span-full hr-text-right">
                    <button type="submit" class="hr-btn hr-btn-primary hr-btn-sm" :disabled="savingSubUnit || !selectedUnitIdForSubUnits">
                      <span v-if="savingSubUnit" class="hr-spinner small"></span>
                      <i v-else class="bx bx-plus"></i> Add Sub-vote
                    </button>
                  </div>
                </form>

                <div v-if="orgSubUnits.length === 0" class="hr-text-muted hr-small">
                  No sub-votes defined for the selected unit.
                </div>
                <ul v-else class="org-list">
                  <li v-for="su in orgSubUnits" :key="su.id" class="hr-d-flex hr-align-items-center hr-justify-content-between hr-gap-2">
                    <template v-if="editingSubUnitId === su.id">
                      <div class="hr-d-flex hr-gap-2 hr-flex-wrap hr-flex-grow-1">
                        <input v-model="editSubUnitForm.name" class="hr-input hr-input-sm" placeholder="Name">
                        <input v-model="editSubUnitForm.code" class="hr-input hr-input-sm" placeholder="Code">
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="saveSubUnitEdit" :disabled="savingSubUnitEdit">Save</button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="editingSubUnitId = null">Cancel</button>
                      </div>
                    </template>
                    <template v-else>
                      <span><strong>{{ su.name }}</strong><span v-if="su.code" class="org-tag">{{ su.code }}</span></span>
                      <div class="hr-d-flex hr-gap-1">
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="startEditSubUnit(su)"><i class="bx bx-edit"></i></button>
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="deleteSubUnitConfirm(su)"><i class="bx bx-trash"></i></button>
                      </div>
                    </template>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <button type="button" class="hr-btn hr-btn-secondary" @click="$refs.orgStructureModal?.close()">Close</button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { execute, authUrl } from "@/api";
import moment from "moment/moment";
import * as XLSX from 'xlsx';

export default {
  name: "DepartmentManagement",
  components: {
    HrPageLayout,
    ModalWrapper
  },
  data() {
    return {
      departments: [],
      filteredDepartments: [],
      locations: [],
      selectedLocation: "",
      search: "",
      selectedDepartment: null,
      departmentEmployees: [],
      departmentToDelete: null,
      editingDepartment: null,
      availableManagers: [],
      selectedManagerInfo: {
        new: null,
        edit: null
      },
      newDepartment: {
        departmentName: '',
        departmentCode: '',
        location: '',
        managerId: '',
        budget: '',
        currency: 'KES',
        description: ''
      },
      stats: {
        totalDepartments: 0,
        activeDepartments: 0,
        totalEmployees: 0,
        averageBudget: 0
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10
      },
      loading: false,
      loadingManagers: false,
      addingDepartment: false,
      updatingDepartment: false,
      deletingDepartment: false,
      loadingEmployees: false,
      error: null,

      // Org structure modal state
      orgStructureDepartment: null,
      orgSections: [],
      orgUnits: [],
      orgSubUnits: [],
      selectedSectionIdForUnits: "",
      selectedUnitIdForSubUnits: "",
      newSection: {
        name: "",
        code: "",
        sub_vote_code: ""
      },
      newUnit: {
        name: "",
        code: ""
      },
      newSubUnit: {
        name: "",
        code: ""
      },
      savingSection: false,
      savingUnit: false,
      savingSubUnit: false,
      editingSectionId: null,
      editSectionForm: { name: "", code: "", sub_vote_code: "" },
      savingSectionEdit: false,
      editingUnitId: null,
      editUnitForm: { name: "", code: "" },
      savingUnitEdit: false,
      editingSubUnitId: null,
      editSubUnitForm: { name: "", code: "" },
      savingSubUnitEdit: false
    };
  },
  mounted() {
    this.fetchDepartments();
    this.fetchAvailableManagers();
  },
  methods: {
    fetchDepartments() {
      this.loading = true;
      this.departments = [];
      this.filteredDepartments = [];

      const data = new FormData();
      data.append("function", "getDepartmentsWithDetails");
      data.append("page", this.pagination.currentPage);
      data.append("limit", this.pagination.limit);

      if (this.search) {
        data.append("search", this.search);
      }

      if (this.selectedLocation) {
        data.append("location", this.selectedLocation);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.departments = Array.isArray(res.data.data.departments) ? res.data.data.departments : [];
              this.stats = res.data.data.stats || {
                totalDepartments: 0,
                activeDepartments: 0,
                totalEmployees: 0,
                averageBudget: 0
              };
              this.locations = Array.isArray(res.data.data.locations) ? res.data.data.locations : [];
              this.pagination = res.data.data.pagination || this.pagination;
              this.filterDepartments();
            } else {
              this.error = res.data.message || "Failed to fetch departments";
              this.departments = [];
              this.filteredDepartments = [];
            }
          })
          .catch((e) => {
            this.loading = false;
            this.error = e.message || "An error occurred while fetching departments";
            console.error("Error fetching departments:", e);
            this.departments = [];
            this.filteredDepartments = [];
          });
    },

    fetchAvailableManagers() {
      this.loadingManagers = true;
      this.availableManagers = [];

      const data = new FormData();
      data.append("function", "getPotentialManagers");

      execute(data, authUrl)
          .then((res) => {
            this.loadingManagers = false;
            if (res.data.success) {
              this.availableManagers = Array.isArray(res.data.data.managers) ? res.data.data.managers : [];
            } else {
              this.error = res.data.message || "Failed to fetch potential managers";
              this.availableManagers = [];
            }
          })
          .catch((e) => {
            this.loadingManagers = false;
            this.error = e.message || "An error occurred while fetching managers";
            console.error("Error fetching managers:", e);
            this.availableManagers = [];
          });
    },

    onManagerChange(type) {
      const managerId = type === 'new' ? this.newDepartment.managerId : this.editingDepartment.managerId;

      if (!managerId) {
        this.selectedManagerInfo[type] = null;
        return;
      }

      // Find the selected manager from the available managers list
      const selectedManager = this.availableManagers.find(manager => manager.id == managerId);
      if (selectedManager) {
        this.selectedManagerInfo[type] = selectedManager;
      } else {
        // If not found in the list, fetch from API
        this.getManagerDetails(managerId, type);
      }
    },

    getManagerDetails(managerId, type) {
      const data = new FormData();
      data.append("function", "getManagerDetails");
      data.append("managerId", managerId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedManagerInfo[type] = res.data.data.manager;
            } else {
              this.selectedManagerInfo[type] = null;
              console.error("Failed to fetch manager details:", res.data.message);
            }
          })
          .catch((e) => {
            this.selectedManagerInfo[type] = null;
            console.error("Error fetching manager details:", e);
          });
    },

    openAddModal() {
      this.newDepartment = {
        departmentName: '',
        location: '',
        managerId: '',
        budget: '',
        currency: 'KES',
        description: ''
      };
      this.selectedManagerInfo.new = null;
      if (this.availableManagers.length === 0) this.fetchAvailableManagers();
      this.$nextTick(() => this.$refs.addDepartmentModal?.open());
    },

    openEditFromView() {
      this.$refs.viewDepartmentModal?.close();
      this.editDepartment(this.selectedDepartment);
    },

    filterDepartments() {
      if (!Array.isArray(this.departments)) {
        this.filteredDepartments = [];
        return;
      }

      let filtered = [...this.departments];

      // Filter by search term
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(dept =>
            (dept.departmentName && dept.departmentName.toLowerCase().includes(searchLower)) ||
            (dept.manager && dept.manager.toLowerCase().includes(searchLower)) ||
            (dept.location && dept.location.toLowerCase().includes(searchLower))
        );
      }

      this.filteredDepartments = filtered;
    },

    viewDepartment(department) {
      this.selectedDepartment = { ...department };
      this.getDepartmentEmployees(department.id);
      this.$nextTick(() => this.$refs.viewDepartmentModal?.open());
    },

    getDepartmentEmployees(departmentId) {
      this.loadingEmployees = true;
      this.departmentEmployees = [];

      const data = new FormData();
      data.append("function", "getDepartmentEmployees");
      data.append("departmentId", departmentId);

      execute(data, authUrl)
          .then((res) => {
            this.loadingEmployees = false;
            if (res.data.success) {
              this.departmentEmployees = Array.isArray(res.data.data.employees) ? res.data.data.employees : [];
            } else {
              this.error = res.data.message || "Failed to fetch department employees";
              this.departmentEmployees = [];
            }
          })
          .catch((e) => {
            this.loadingEmployees = false;
            this.error = e.message || "An error occurred while fetching employees";
            console.error("Error fetching employees:", e);
            this.departmentEmployees = [];
          });
    },

    editDepartment(department) {
      this.editingDepartment = { ...department };
      this.editingDepartment.departmentCode = department.code ?? department.departmentCode ?? '';
      this.selectedManagerInfo.edit = null;
      if (department.manager) {
        const manager = this.availableManagers.find(m =>
            `${m.firstName} ${m.lastName}` === department.manager
        );
        if (manager) {
          this.editingDepartment.managerId = manager.id;
          this.selectedManagerInfo.edit = manager;
        }
      }
      this.$nextTick(() => this.$refs.editDepartmentModal?.open());
    },

    addDepartment() {
      if (!this.newDepartment.departmentName.trim()) {
        alert("Department name is required");
        return;
      }

      this.addingDepartment = true;

      const data = new FormData();
      data.append("function", "addDepartmentWithManager");
      Object.keys(this.newDepartment).forEach(key => {
        data.append(key, this.newDepartment[key]);
      });

      execute(data, authUrl)
          .then((res) => {
            this.addingDepartment = false;
            if (res.data.success) {
              this.$refs.addDepartmentModal?.close();
              // Reset form
              this.newDepartment = {
                departmentName: '',
                departmentCode: '',
                location: '',
                managerId: '',
                budget: '',
                currency: 'KES',
                description: ''
              };
              this.selectedManagerInfo.new = null;

              // Refresh departments
              this.fetchDepartments();

              // Show success message
              this.showSuccessMessage("Department added successfully");
            } else {
              this.error = res.data.message || "Failed to add department";
              alert(this.error);
            }
          })
          .catch((e) => {
            this.addingDepartment = false;
            this.error = e.message || "An error occurred while adding department";
            console.error("Error adding department:", e);
            alert(this.error);
          });
    },

    updateDepartment() {
      if (!this.editingDepartment.departmentName.trim()) {
        alert("Department name is required");
        return;
      }

      this.updatingDepartment = true;

      const data = new FormData();
      data.append("function", "updateDepartmentWithManager");
      Object.keys(this.editingDepartment).forEach(key => {
        data.append(key, this.editingDepartment[key]);
      });

      execute(data, authUrl)
          .then((res) => {
            this.updatingDepartment = false;
            if (res.data.success) {
              this.$refs.editDepartmentModal?.close();
              // Refresh departments
              this.fetchDepartments();

              // Show success message
              this.showSuccessMessage("Department updated successfully");

              this.editingDepartment = null;
              this.selectedManagerInfo.edit = null;
            } else {
              this.error = res.data.message || "Failed to update department";
              alert(this.error);
            }
          })
          .catch((e) => {
            this.updatingDepartment = false;
            this.error = e.message || "An error occurred while updating department";
            console.error("Error updating department:", e);
            alert(this.error);
          });
    },

    confirmDelete(department) {
      this.departmentToDelete = department;
      this.$nextTick(() => this.$refs.deleteConfirmModal?.open());
    },

    deleteDepartment() {
      if (!this.departmentToDelete) return;

      this.deletingDepartment = true;

      const data = new FormData();
      data.append("function", "deleteDepartment");
      data.append("departmentId", this.departmentToDelete.id);

      execute(data, authUrl)
          .then((res) => {
            this.deletingDepartment = false;
            if (res.data.success) {
              this.$refs.deleteConfirmModal?.close();
              // Refresh departments
              this.fetchDepartments();

              // Show success message
              this.showSuccessMessage("Department deleted successfully");

              this.departmentToDelete = null;
            } else {
              this.error = res.data.message || "Failed to delete department";
              alert(this.error);
            }
          })
          .catch((e) => {
            this.deletingDepartment = false;
            this.error = e.message || "An error occurred while deleting department";
            console.error("Error deleting department:", e);
            alert(this.error);
          });
    },

    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page;
        this.fetchDepartments();
      }
    },

    getVisiblePages() {
      const current = this.pagination.currentPage;
      const total = this.pagination.totalPages;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (let i = Math.max(2, current - delta);
           i <= Math.min(total - 1, current + delta);
           i++) {
        range.push(i);
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...');
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total);
      } else {
        rangeWithDots.push(total);
      }

      return rangeWithDots.filter((page, index, arr) => arr.indexOf(page) === index);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD MMM YYYY');
    },

    formatCurrency(amount) {
      if (!amount || isNaN(amount)) return 'N/A';
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES'
      }).format(amount);
    },

    getStatusBadgeClass(status) {
      switch (status?.toLowerCase()) {
        case 'active': return 'hr-badge-success';
        case 'inactive': return 'hr-badge-secondary';
        case 'suspended': return 'hr-badge-warning';
        default: return 'hr-badge-success';
      }
    },

    getEmployeeStatusBadge(status) {
      switch (status?.toLowerCase()) {
        case 'active': return 'hr-badge-success';
        case 'inactive': return 'hr-badge-secondary';
        case 'suspended': return 'hr-badge-warning';
        case 'terminated': return 'hr-badge-danger';
        default: return 'hr-badge-primary';
      }
    },

    showSuccessMessage(message) {
      // You can implement a toast notification here
      alert(message);
    },

    downloadReport() {
      try {
        if (!Array.isArray(this.departments) || this.departments.length === 0) {
          alert("No data available for download.");
          return;
        }

        // Create worksheet data
        const worksheetData = [];

        // Add headers
        worksheetData.push([
          'Department ID',
          'Department Name',
          'Vote',
          'Location',
          'Authorised Chief Officer',
          'Authorised Chief Officer Email',
          'Authorised Chief Officer Phone',
          'Budget',
          'Currency',
          'Employee Count',
          'Status',
          'Created Date',
          'Description'
        ]);

        // Add data rows
        this.filteredDepartments.forEach(department => {
          worksheetData.push([
            department.id || '',
            department.departmentName || '',
            department.code || '',
            department.location || '',
            department.manager || '',
            department.managerEmail || '',
            department.managerPhone || '',
            department.budget || '',
            department.currency || 'KES',
            department.employeeCount || 0,
            department.status || 'Active',
            this.formatDate(department.createdAt),
            department.description || ''
          ]);
        });

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        // Set column widths
        const wscols = [
          { wch: 10 }, // Department ID
          { wch: 25 }, // Department Name
          { wch: 12 }, // Vote
          { wch: 20 }, // Location
          { wch: 25 }, // Authorised Chief Officer
          { wch: 28 }, // Authorised Chief Officer Email
          { wch: 20 }, // Authorised Chief Officer Phone
          { wch: 15 }, // Budget
          { wch: 10 }, // Currency
          { wch: 12 }, // Employee Count
          { wch: 10 }, // Status
          { wch: 15 }, // Created Date
          { wch: 30 }  // Description
        ];
        ws['!cols'] = wscols;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Departments");

        // Generate filename
        const fileName = `Departments_Report_${moment().format('YYYY-MM-DD')}.xlsx`;

        // Write and download the file
        XLSX.writeFile(wb, fileName);
      
      } catch (error) {
        console.error('Error generating Excel file:', error);
        alert('Failed to generate Excel file. Please try again.');
      }
    },

    // Org structure management
    openOrgStructure(department) {
      this.orgStructureDepartment = { ...department };
      this.orgSections = [];
      this.orgUnits = [];
      this.orgSubUnits = [];
      this.selectedSectionIdForUnits = "";
      this.selectedUnitIdForSubUnits = "";
      this.newSection = { name: "", code: "", sub_vote_code: "" };
      this.newUnit = { name: "", code: "" };
      this.newSubUnit = { name: "", code: "" };
      this.editingSectionId = null;
      this.editingUnitId = null;
      this.editingSubUnitId = null;
      this.loadSectionsForDepartment();
      this.$nextTick(() => this.$refs.orgStructureModal?.open());
    },

    loadSectionsForDepartment() {
      if (!this.orgStructureDepartment) return;
      const data = new FormData();
      data.append("function", "getSectionsByDepartment");
      data.append("departmentId", this.orgStructureDepartment.id);
      execute(data, authUrl)
        .then(res => {
          this.orgSections = res?.data?.data?.sections || res?.data?.sections || [];
        })
        .catch(e => {
          console.error("Error loading sections:", e);
          this.orgSections = [];
        });
    },

    loadUnitsForSection() {
      this.orgUnits = [];
      this.orgSubUnits = [];
      if (!this.selectedSectionIdForUnits) return;
      const data = new FormData();
      data.append("function", "getUnitsBySection");
      data.append("sectionId", this.selectedSectionIdForUnits);
      execute(data, authUrl)
        .then(res => {
          this.orgUnits = res?.data?.data?.units || res?.data?.units || [];
        })
        .catch(e => {
          console.error("Error loading units:", e);
          this.orgUnits = [];
        });
    },

    loadSubUnitsForUnit() {
      this.orgSubUnits = [];
      if (!this.selectedUnitIdForSubUnits) return;
      const data = new FormData();
      data.append("function", "getSubUnitsByUnit");
      data.append("unitId", this.selectedUnitIdForSubUnits);
      execute(data, authUrl)
        .then(res => {
          this.orgSubUnits = res?.data?.data?.subUnits || res?.data?.subUnits || [];
        })
        .catch(e => {
          console.error("Error loading sub units:", e);
          this.orgSubUnits = [];
        });
    },

    createSectionForDept() {
      if (!this.orgStructureDepartment || !this.newSection.name.trim()) {
        alert("Section name is required");
        return;
      }
      this.savingSection = true;
      const data = new FormData();
      data.append("function", "createSection");
      data.append("departmentId", this.orgStructureDepartment.id);
      data.append("name", this.newSection.name);
      data.append("code", this.newSection.code);
      data.append("sub_vote_code", this.newSection.sub_vote_code);
      execute(data, authUrl)
        .then(res => {
          this.savingSection = false;
          if (res.data.success) {
            this.newSection = { name: "", code: "", sub_vote_code: "" };
            this.loadSectionsForDepartment();
          } else {
            alert(res.data.message || "Failed to create section");
          }
        })
        .catch(e => {
          this.savingSection = false;
          console.error("Error creating section:", e);
          alert("Error creating section");
        });
    },

    createUnitForSection() {
      if (!this.selectedSectionIdForUnits) {
        alert("Select a section first");
        return;
      }
      if (!this.newUnit.name.trim()) {
        alert("Unit name is required");
        return;
      }
      this.savingUnit = true;
      const data = new FormData();
      data.append("function", "createUnit");
      data.append("sectionId", this.selectedSectionIdForUnits);
      data.append("name", this.newUnit.name);
      data.append("code", this.newUnit.code);
      execute(data, authUrl)
        .then(res => {
          this.savingUnit = false;
          if (res.data.success) {
            this.newUnit = { name: "", code: "" };
            this.loadUnitsForSection();
          } else {
            alert(res.data.message || "Failed to create unit");
          }
        })
        .catch(e => {
          this.savingUnit = false;
          console.error("Error creating unit:", e);
          alert("Error creating unit");
        });
    },

    createSubUnitForUnit() {
      if (!this.selectedUnitIdForSubUnits) {
        alert("Select a unit first");
        return;
      }
      if (!this.newSubUnit.name.trim()) {
        alert("Sub-vote name is required");
        return;
      }
      this.savingSubUnit = true;
      const data = new FormData();
      data.append("function", "createSubUnit");
      data.append("unitId", this.selectedUnitIdForSubUnits);
      data.append("name", this.newSubUnit.name);
      data.append("code", this.newSubUnit.code);
      execute(data, authUrl)
        .then(res => {
          this.savingSubUnit = false;
          if (res.data.success) {
            this.newSubUnit = { name: "", code: "" };
            this.loadSubUnitsForUnit();
          } else {
            alert(res.data.message || "Failed to create sub-vote");
          }
        })
        .catch(e => {
          this.savingSubUnit = false;
          console.error("Error creating sub-vote:", e);
          alert("Error creating sub-vote");
        });
    },

    startEditSection(sec) {
      this.editingSectionId = sec.id;
      this.editSectionForm = { name: sec.name || "", code: sec.code || "", sub_vote_code: sec.sub_vote_code || "" };
    },
    saveSectionEdit() {
      if (!this.editSectionForm.name.trim()) {
        alert("Section name is required");
        return;
      }
      this.savingSectionEdit = true;
      const data = new FormData();
      data.append("function", "updateSection");
      data.append("sectionId", this.editingSectionId);
      data.append("name", this.editSectionForm.name);
      data.append("code", this.editSectionForm.code);
      data.append("sub_vote_code", this.editSectionForm.sub_vote_code);
      execute(data, authUrl)
        .then(res => {
          this.savingSectionEdit = false;
          if (res.data.success) {
            this.editingSectionId = null;
            this.loadSectionsForDepartment();
          } else {
            alert(res.data.message || "Failed to update section");
          }
        })
        .catch(e => {
          this.savingSectionEdit = false;
          console.error("Error updating section:", e);
          alert("Error updating section");
        });
    },
    deleteSectionConfirm(sec) {
      if (!confirm(`Delete section "${sec.name}"? This will deactivate it.`)) return;
      const data = new FormData();
      data.append("function", "deleteSection");
      data.append("sectionId", sec.id);
      execute(data, authUrl)
        .then(res => {
          if (res.data.success) {
            this.loadSectionsForDepartment();
            if (this.selectedSectionIdForUnits == sec.id) {
              this.selectedSectionIdForUnits = "";
              this.orgUnits = [];
              this.orgSubUnits = [];
            }
          } else {
            alert(res.data.message || "Failed to delete section");
          }
        })
        .catch(e => {
          console.error("Error deleting section:", e);
          alert("Error deleting section");
        });
    },

    startEditUnit(u) {
      this.editingUnitId = u.id;
      this.editUnitForm = { name: u.name || "", code: u.code || "" };
    },
    saveUnitEdit() {
      if (!this.editUnitForm.name.trim()) {
        alert("Unit name is required");
        return;
      }
      this.savingUnitEdit = true;
      const data = new FormData();
      data.append("function", "updateUnit");
      data.append("unitId", this.editingUnitId);
      data.append("name", this.editUnitForm.name);
      data.append("code", this.editUnitForm.code);
      execute(data, authUrl)
        .then(res => {
          this.savingUnitEdit = false;
          if (res.data.success) {
            this.editingUnitId = null;
            this.loadUnitsForSection();
          } else {
            alert(res.data.message || "Failed to update unit");
          }
        })
        .catch(e => {
          this.savingUnitEdit = false;
          console.error("Error updating unit:", e);
          alert("Error updating unit");
        });
    },
    deleteUnitConfirm(u) {
      if (!confirm(`Delete unit "${u.name}"? This will deactivate it.`)) return;
      const data = new FormData();
      data.append("function", "deleteUnit");
      data.append("unitId", u.id);
      execute(data, authUrl)
        .then(res => {
          if (res.data.success) {
            this.loadUnitsForSection();
            if (this.selectedUnitIdForSubUnits == u.id) {
              this.selectedUnitIdForSubUnits = "";
              this.orgSubUnits = [];
            }
          } else {
            alert(res.data.message || "Failed to delete unit");
          }
        })
        .catch(e => {
          console.error("Error deleting unit:", e);
          alert("Error deleting unit");
        });
    },

    startEditSubUnit(su) {
      this.editingSubUnitId = su.id;
      this.editSubUnitForm = { name: su.name || "", code: su.code || "" };
    },
    saveSubUnitEdit() {
      if (!this.editSubUnitForm.name.trim()) {
        alert("Sub-vote name is required");
        return;
      }
      this.savingSubUnitEdit = true;
      const data = new FormData();
      data.append("function", "updateSubUnit");
      data.append("subUnitId", this.editingSubUnitId);
      data.append("name", this.editSubUnitForm.name);
      data.append("code", this.editSubUnitForm.code);
      execute(data, authUrl)
        .then(res => {
          this.savingSubUnitEdit = false;
          if (res.data.success) {
            this.editingSubUnitId = null;
            this.loadSubUnitsForUnit();
          } else {
            alert(res.data.message || "Failed to update sub-vote");
          }
        })
        .catch(e => {
          this.savingSubUnitEdit = false;
          console.error("Error updating sub-vote:", e);
          alert("Error updating sub-vote");
        });
    },
    deleteSubUnitConfirm(su) {
      if (!confirm(`Delete sub-vote "${su.name}"? This will deactivate it.`)) return;
      const data = new FormData();
      data.append("function", "deleteSubUnit");
      data.append("subUnitId", su.id);
      execute(data, authUrl)
        .then(res => {
          if (res.data.success) this.loadSubUnitsForUnit();
          else alert(res.data.message || "Failed to delete sub-vote");
        })
        .catch(e => {
          console.error("Error deleting sub-vote:", e);
          alert("Error deleting sub-vote");
        });
    }
  }
};
</script>

<style src="@/assets/css/hr-page-shared.css"></style>
<style scoped>
.hr-filters-row { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 16px; }
.hr-search-wrap { flex: 1; min-width: 200px; }
.dept-cell-name { display: flex; align-items: center; gap: 12px; }
.dept-avatar { width: 40px; height: 40px; border-radius: 50%; background: rgba(5,150,105,0.15); color: #059669; display: flex; align-items: center; justify-content: center; font-weight: 600; flex-shrink: 0; }
.hr-btn-group { display: flex; gap: 8px; flex-wrap: wrap; }
.hr-budget-input { display: flex; gap: 8px; align-items: stretch; }
.hr-budget-input .hr-select { flex-shrink: 0; }
.hr-budget-input .hr-input { flex: 1; min-width: 0; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }
.hr-text-danger { color: #ef4444; }
.hr-text-info { color: #06b6d4; }
.hr-d-block { display: block; }
.hr-mt-1 { margin-top: 4px; }
.hr-mt-3 { margin-top: 12px; }
.hr-mb-3 { margin-bottom: 12px; }
.hr-py-4 { padding-top: 16px; padding-bottom: 16px; }
.hr-p-0 { padding: 0; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
.dept-view-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
.dept-view-item { margin-bottom: 12px; }
.dept-view-item:last-child { margin-bottom: 0; }
.dept-view-item label { display: block; font-size: 0.8rem; margin-bottom: 4px; }
.hr-small { font-size: 0.85rem; }
.hr-spinner.small { width: 18px; height: 18px; border-width: 2px; display: inline-block; vertical-align: middle; margin-right: 6px; }
.bx-spin { animation: spin 1s linear infinite; }
.hr-table-striped tbody tr:nth-child(even) { background: #f8fafc; }
.hr-table-hover tbody tr:hover { background: rgba(5,150,105,0.05); }
.hr-d-flex { display: flex; }
.hr-align-items-center { align-items: center; }
.hr-justify-content-between { justify-content: space-between; }
.hr-flex-grow-1 { flex-grow: 1; }
.hr-flex-wrap { flex-wrap: wrap; }
.hr-input-sm { padding: 6px 8px; font-size: 0.875rem; }
.org-list { list-style: none; padding: 0; margin: 0; }
.org-list li { margin-bottom: 8px; }
.org-tag { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 0.8rem; background: rgba(5,150,105,0.12); color: #059669; margin-left: 6px; }
.org-tag.muted { background: #f1f5f9; color: #64748b; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>